// @ts-ignore - This import is needed for Supabase migration
import { supabase } from '@/lib/supabase';
import { useState, useEffect } from 'react'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Checkbox } from '@/components/ui/checkbox'
import { Label } from '@/components/ui/label'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import { toast } from 'sonner'
import {
  Check,
  X,
  FileEdit,
  DollarSign,
  AlertTriangle,
  CalendarIcon,
  CreditCard,
  ChevronDown,
  Settings,
  Target,
  ArrowRightLeft,
  CheckCircle
} from 'lucide-react'
import { CategorySelect } from './CategorySelect'
import { UpdateConfirmationDialog } from './UpdateConfirmationDialog'
import { UpdateProgressTracker } from './UpdateProgressTracker'
import { cn } from '@/lib/utils'
import { format } from 'date-fns'
import { AccountSelect } from './AccountSelect'
import { PayeeSelect } from './PayeeSelect'
import { TagSelect } from './TagSelect'
import { useTransactionStore } from '@/store/transactionsStore'
import { useAccountsStore } from '@/store/accountsStore'
import { useAuthStore } from '@/store/authStore'
import { useTransactionStore as useCoreTransactionStore, useCurrencyStore } from '@flows.cash/core'
import { useQueryClient } from '@tanstack/react-query'
import { invalidateTransactions } from '@/utils/queryUtils'
import { ExchangeRateInput } from './ExchangeRateInput'
import { StyledModal, StyledModalSection, StyledModalButton } from '@/components/ui/styled-modal'
import { findPotentialTransferMatches, PotentialTransferMatch } from './utils/transferDetection'

// Define temporary interfaces until proper imports can be resolved
interface Transaction {
  id: string
  description?: string
  categoryId?: string
  accountId?: string
  account_id?: string
  tags?: string[]
  type?: 'expense' | 'income' | 'transfer'
  date?: Date
  notes?: string
  amount?: number
  status?: 'pending' | 'completed' | 'cancelled'
  payeeId?: string
}

// Functional DatePicker that uses a simple input for now
const DatePicker = ({ 
  date, 
  setDate,
  className,
  minDate,
  maxDate
}: { 
  date: Date | null, 
  setDate: (date: Date | null) => void,
  className?: string,
  minDate?: Date,
  maxDate?: Date
}) => {
  // Format date as YYYY-MM-DD for the input
  const dateValue = date ? format(date, 'yyyy-MM-dd') : '';
  
  // Handle date change from input
  const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (!value) {
      setDate(null);
      return;
    }
    
    const newDate = new Date(value);
    
    // Check if date is valid
    if (isNaN(newDate.getTime())) {
      return;
    }
    
    // Check min/max constraints
    if (minDate && newDate < minDate) return;
    if (maxDate && newDate > maxDate) return;
    
    setDate(newDate);
  };
  
  return (
    <div className="relative">
      <div className="flex items-center relative">
        <CalendarIcon className="w-4 h-4 text-secondary dark:text-primary-dark absolute left-3" />
        <Input
          type="date"
          value={dateValue}
          onChange={handleDateChange}
          className={cn(
            "pl-9 h-11 w-full rounded-xl border border-border bg-background-input shadow-sm",
            "focus:ring-2 focus:ring-secondary",
            "dark:border-border-dark dark:bg-background-dark-input dark:focus:ring-primary-dark",
            className
          )}
          min={minDate ? format(minDate, 'yyyy-MM-dd') : undefined}
          max={maxDate ? format(maxDate, 'yyyy-MM-dd') : undefined}
        />
      </div>
    </div>
  );
}

interface MassUpdateTransactionsProps {
  isOpen: boolean
  onClose: () => void
  selectedTransactions: Transaction[]
  onUpdated?: () => void // Callback to trigger refresh after successful update
}

interface UpdateFields {
  category: boolean
  account: boolean
  tags: boolean
  type: boolean
  description: boolean
  date: boolean
  notes: boolean
  amount: boolean
  status: boolean
  payee: boolean
}

interface UpdateData {
  categoryId: string
  category: string
  accountId: string
  tags: string[]
  type: 'expense' | 'income' | 'transfer'
  description: string
  date: Date | null
  notes: string
  amount: string
  status: 'pending' | 'completed' | 'cancelled'
  payeeId: string
  toAccountId: string
  exchangeRate: number
}

interface UpdateProgress {
  current: number
  total: number
  successCount: number
  failedCount: number
  currentTransaction: string
}

export function MassUpdateTransactions({
  isOpen,
  onClose,
  selectedTransactions,
  onUpdated
}: MassUpdateTransactionsProps) {
  const { updateTransaction, fetchTransactions, transactions } = useTransactionStore()
  const { user } = useAuthStore()
  const { accounts } = useAccountsStore()
  const coreTransactionStore = useCoreTransactionStore()
  const { convertAmount: coreConvertAmount } = useCurrencyStore()
  const queryClient = useQueryClient()
  
  // State for fields to update
  const [selectedFields, setSelectedFields] = useState<UpdateFields>({
    category: false,
    account: false,
    tags: false,
    type: false,
    description: false,
    date: false,
    notes: false,
    amount: false,
    status: false,
    payee: false
  })
  
  // State for update data
  const [updateData, setUpdateData] = useState<UpdateData>({
    categoryId: '',
    category: '',
    accountId: '',
    tags: [],
    type: 'expense',
    description: '',
    date: null,
    notes: '',
    amount: '',
    status: 'completed',
    payeeId: '',
    toAccountId: '',
    exchangeRate: 1
  })
  
  // Validation state
  const [errors, setErrors] = useState<Partial<Record<keyof UpdateData, string>>>({})
  
  // Update progress tracking
  const [updateProgress, setUpdateProgress] = useState<UpdateProgress | null>(null)
  
  // Confirmation dialog state
  const [showConfirmation, setShowConfirmation] = useState(false)
  
  // Update state
  const [isUpdating, setIsUpdating] = useState(false)
  const [isCompleted, setIsCompleted] = useState(false)
  const [completionSummary, setCompletionSummary] = useState<{
    type: 'update' | 'transfer'
    successCount: number
    failedCount: number
    details: string[]
  } | null>(null)
  
  // Transfer detection state
  const [detectedMatches, setDetectedMatches] = useState<{ [transactionId: string]: PotentialTransferMatch[] }>({})
  const [showTransferDetectionSummary, setShowTransferDetectionSummary] = useState(false)
  const [isDetectingTransfers, setIsDetectingTransfers] = useState(false)
  
  // Auto-fetch exchange rates when accounts change for transfers
  useEffect(() => {
    const autoFetchExchangeRate = async () => {
      if (updateData.type !== 'transfer' || !updateData.accountId || !updateData.toAccountId) {
        return;
      }

      const fromAccount = accounts.find((acc: any) => acc.id === updateData.accountId);
      const toAccount = accounts.find((acc: any) => acc.id === updateData.toAccountId);
      
      if (!fromAccount?.currency || !toAccount?.currency || fromAccount.currency === toAccount.currency) {
        // Same currency, set rate to 1
        setUpdateData(prev => ({ ...prev, exchangeRate: 1 }));
        return;
      }

      try {
        let rate: number;
        try {
          rate = coreConvertAmount(1, fromAccount.currency, toAccount.currency);
        } catch (error) {
          console.error('Error getting exchange rate:', error);
          rate = 1; // Default to 1:1 rate if conversion fails
        }

        if (rate && !isNaN(rate)) {
          const roundedRate = Math.round(rate * 1000000) / 1000000; // 6 decimal places
          setUpdateData(prev => ({ ...prev, exchangeRate: roundedRate }));
        }
      } catch (error) {
        console.error('Error auto-fetching exchange rate:', error);
      }
    };

    autoFetchExchangeRate();
  }, [updateData.type, updateData.accountId, updateData.toAccountId, accounts, coreConvertAmount]);
  
  // Reset state when modal is opened/closed
  useEffect(() => {
    if (!isOpen) {
      setSelectedFields({
        category: false,
        account: false,
        tags: false,
        type: false,
        description: false,
        date: false,
        notes: false,
        amount: false,
        status: false,
        payee: false
      })
      
      setUpdateData({
        categoryId: '',
        category: '',
        accountId: '',
        tags: [],
        type: 'expense',
        description: '',
        date: null,
        notes: '',
        amount: '',
        status: 'completed',
        payeeId: '',
        toAccountId: '',
        exchangeRate: 1
      })
      
      setErrors({})
      setUpdateProgress(null)
      setIsUpdating(false)
      setIsCompleted(false)
      setCompletionSummary(null)
      setDetectedMatches({})
      setShowTransferDetectionSummary(false)
      setIsDetectingTransfers(false)
    }
  }, [isOpen])
  
  // Validate transfer conditions
  const validateTransferConditions = (): boolean => {
    if (updateData.type !== 'transfer') return true;
    
    // Check if all selected transactions are from the same account
    const accountIds = new Set(selectedTransactions.map(t => t.accountId || t.account_id).filter(Boolean));
    if (accountIds.size > 1) {
      setErrors(prev => ({
        ...prev,
        type: 'All transactions must be from the same account to convert to transfer'
      }));
      return false;
    }
    
    // Check if all transactions have the same direction (all positive or all negative)
    const amounts = selectedTransactions.map(t => t.amount).filter(a => a !== undefined);
    if (amounts.length === 0) return true;
    
    const allPositive = amounts.every(amount => amount! > 0);
    const allNegative = amounts.every(amount => amount! < 0);
    
    if (!allPositive && !allNegative) {
      setErrors(prev => ({
        ...prev,
        type: 'All transactions must have the same direction (all income or all expenses) to convert to transfer'
      }));
      return false;
    }
    
    return true;
  };

  // Handler for toggling fields to update
  const handleFieldToggle = (field: keyof UpdateFields, checked: boolean) => {
    setSelectedFields(prev => ({
      ...prev,
      [field]: checked
    }))
    
    // Clear any errors for this field
    if (!checked && errors[field as keyof UpdateData]) {
      setErrors(prev => {
        const newErrors = {...prev}
        delete newErrors[field as keyof UpdateData]
        return newErrors
      })
    }
  }
  
  // Handler for category changes
  const handleCategoryChange = (categoryId: string, categoryName: string) => {
    setUpdateData(prev => ({
      ...prev,
      categoryId,
      category: categoryName
    }))
  }
  
  // Handler for account changes
  const handleAccountChange = (accountId: string) => {
    setUpdateData(prev => ({
      ...prev,
      accountId
    }))
  }
  
  // Handler for tags changes
  const handleTagsChange = (tags: string[]) => {
    setUpdateData(prev => ({
      ...prev,
      tags
    }))
  }
  
  // Handler for transaction type changes
  const handleTypeChange = (type: 'expense' | 'income' | 'transfer') => {
    setUpdateData(prev => ({
      ...prev,
      type
    }))
    
    // Validate transfer conditions if switching to transfer
    if (type === 'transfer') {
      setTimeout(() => validateTransferConditions(), 100);
      
      // Trigger transfer detection if we already have a destination account selected
      if (updateData.toAccountId) {
        setTimeout(() => {
          runTransferDetection(updateData.toAccountId);
        }, 200);
      }
    } else {
      // Clear detection results when switching away from transfer
      setDetectedMatches({});
      setShowTransferDetectionSummary(false);
    }
  }
  
  // Handler for text field changes
  const handleTextChange = (field: keyof UpdateData, value: string) => {
    setUpdateData(prev => ({
      ...prev,
      [field]: value
    }))
    
    // Clear any errors for this field
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = {...prev}
        delete newErrors[field]
        return newErrors
      })
    }
  }
  
  // Handler for date changes
  const handleDateChange = (date: Date | null) => {
    setUpdateData(prev => ({
      ...prev,
      date
    }))
  }
  
  // Handler for status changes
  const handleStatusChange = (status: 'pending' | 'completed' | 'cancelled') => {
    setUpdateData(prev => ({
      ...prev,
      status
    }))
  }

  // Handler for to account changes
  const handleToAccountChange = (toAccountId: string) => {
    setUpdateData(prev => ({
      ...prev,
      toAccountId
    }))
    
    // Trigger transfer detection when destination account is selected for transfers
    if (updateData.type === 'transfer' && toAccountId) {
      setTimeout(() => {
        runTransferDetection(toAccountId);
      }, 100);
    }
  }

  // Handler for exchange rate changes
  const handleExchangeRateChange = (exchangeRate: number) => {
    setUpdateData(prev => ({
      ...prev,
      exchangeRate
    }))
    
    // NEW: Re-run transfer detection when exchange rate changes for different currency transfers
    if (updateData.type === 'transfer' && updateData.toAccountId) {
      const fromAccount = accounts.find((acc: any) => acc.id === updateData.accountId);
      const toAccount = accounts.find((acc: any) => acc.id === updateData.toAccountId);
      
      // Only trigger for different currency transfers
      if (fromAccount?.currency && toAccount?.currency && fromAccount.currency !== toAccount.currency) {
        console.log('💱 Exchange rate changed for different currency transfer - triggering detection');
        setTimeout(() => {
          runTransferDetection(updateData.toAccountId);
        }, 300); // Small delay to allow rate to settle
      }
    }
  }

  // Transfer detection function
  const runTransferDetection = async (targetAccountId: string) => {
    if (!user?.id || updateData.type !== 'transfer' || selectedTransactions.length === 0) {
      return;
    }

    setIsDetectingTransfers(true);
    console.log(`🔍 Running transfer detection for ${selectedTransactions.length} transactions to account ${targetAccountId}`);

    try {
      // Get all transactions for detection (smart fetching like in EditTransaction)
      let allTransactionsForDetection = transactions || [];
      
      // Calculate comprehensive date range that covers ALL selected transactions
      const transactionDates = selectedTransactions.map(t => new Date(t.date || Date.now()));
      const oldestDate = new Date(Math.min(...transactionDates.map(d => d.getTime())));
      const newestDate = new Date(Math.max(...transactionDates.map(d => d.getTime())));
      const now = new Date();
      
      console.log(`📊 Processing ${selectedTransactions.length} transactions spanning from ${oldestDate.toDateString()} to ${newestDate.toDateString()}`);
      
      // Check the age of the oldest transaction to determine if we need additional fetching
      const oldestAge = (now.getTime() - oldestDate.getTime()) / (1000 * 60 * 60 * 24);
      
      // If any transaction is older than 30 days, fetch additional transactions
      if (oldestAge > 30) {
        console.log('🔍 Old transactions detected, fetching additional transactions...');
        
        // Calculate date range that covers all transactions with appropriate buffer
        let bufferDays = 7; // Default buffer
        
        if (oldestAge > 365) {
          bufferDays = 30; // Large buffer for very old transactions
          console.log(`📅 Very old transactions (${oldestAge.toFixed(0)} days), using ±${bufferDays} day buffer`);
        } else if (oldestAge > 90) {
          bufferDays = 14; // Medium buffer for old transactions
          console.log(`📅 Old transactions (${oldestAge.toFixed(0)} days), using ±${bufferDays} day buffer`);
        } else {
          console.log(`📅 Recent transactions (${oldestAge.toFixed(0)} days), using ±${bufferDays} day buffer`);
        }
        
        // Create date range that covers ALL transactions + buffer
        const startDate = new Date(oldestDate);
        startDate.setDate(startDate.getDate() - bufferDays);
        const endDate = new Date(newestDate);
        endDate.setDate(endDate.getDate() + bufferDays);
        
        console.log(`📅 Fetching transactions from ${startDate.toISOString()} to ${endDate.toISOString()} (covers all ${selectedTransactions.length} transactions + ${bufferDays} day buffer)`)
        
        const selectedIds = new Set(selectedTransactions.map((t: Transaction) => t.id));
        
        const { data: additionalTransactions, error } = await supabase
          .from('transactions')
          .select('*')
          .eq('user_id', user.id)
          .gte('date', startDate.toISOString())
          .lte('date', endDate.toISOString());
        
        if (error) {
          console.error('Error fetching additional transactions:', error);
        }
        
        if (!error && additionalTransactions) {
          const existingIds = new Set(allTransactionsForDetection.map(t => t.id));
          // Filter out selected transactions and existing transactions
          const newTransactions = additionalTransactions.filter(t => 
            !existingIds.has(t.id) && !selectedIds.has(t.id)
          );
          allTransactionsForDetection = [...allTransactionsForDetection, ...newTransactions];
          console.log(`📊 Total transactions for detection: ${allTransactionsForDetection.length}`);
        }
        
        // CRITICAL: Also fetch specifically from target account in comprehensive date ranges
        if (targetAccountId) {
          console.log(`🏦 Fetching additional transactions from target account: ${targetAccountId}`);
          
          // For better coverage, also fetch from individual transaction date ranges
          const expandedDateRanges = selectedTransactions.map(transaction => {
            const txDate = new Date(transaction.date || Date.now());
            const txAge = (now.getTime() - txDate.getTime()) / (1000 * 60 * 60 * 24);
            
            // Calculate buffer for this specific transaction
            let txBuffer = 7;
            if (txAge > 365) txBuffer = 30;
            else if (txAge > 90) txBuffer = 14;
            
            const txStart = new Date(txDate);
            txStart.setDate(txStart.getDate() - txBuffer);
            const txEnd = new Date(txDate);
            txEnd.setDate(txEnd.getDate() + txBuffer);
            
            return { start: txStart, end: txEnd, transactionId: transaction.id };
          });
          
          // Fetch from the main date range
          const { data: accountTransactions, error: accountError } = await supabase
            .from('transactions')
            .select('*')
            .eq('user_id', user.id)
            .eq('account_id', targetAccountId)
            .gte('date', startDate.toISOString())
            .lte('date', endDate.toISOString());
          
          if (accountError) {
            console.error('Error fetching account transactions:', accountError);
          } else if (accountTransactions && accountTransactions.length > 0) {
            console.log(`✅ Fetched ${accountTransactions.length} transactions from target account (main range)`);
            
            // Merge account transactions, avoiding duplicates
            const currentIds = new Set(allTransactionsForDetection.map(t => t.id));
            const newAccountTransactions = accountTransactions.filter(t => 
              !currentIds.has(t.id) && !selectedIds.has(t.id)
            );
            
            allTransactionsForDetection = [...allTransactionsForDetection, ...newAccountTransactions];
          }
          
          // Also fetch from individual transaction date ranges to ensure we don't miss anything
          for (const range of expandedDateRanges) {
            const { data: individualTransactions, error: individualError } = await supabase
              .from('transactions')
              .select('*')
              .eq('user_id', user.id)
              .eq('account_id', targetAccountId)
              .gte('date', range.start.toISOString())
              .lte('date', range.end.toISOString());
            
            if (!individualError && individualTransactions && individualTransactions.length > 0) {
              const currentIds = new Set(allTransactionsForDetection.map(t => t.id));
              const newIndividualTransactions = individualTransactions.filter(t => 
                !currentIds.has(t.id) && !selectedIds.has(t.id)
              );
              
              if (newIndividualTransactions.length > 0) {
                allTransactionsForDetection = [...allTransactionsForDetection, ...newIndividualTransactions];
                console.log(`✅ Fetched ${newIndividualTransactions.length} additional transactions for individual transaction ${range.transactionId}`);
              }
            }
          }
          
          console.log(`📊 Final total transactions for detection: ${allTransactionsForDetection.length} (comprehensive target account fetching)`);
        }
      }

      // Run detection for each selected transaction
      const newMatches: { [transactionId: string]: PotentialTransferMatch[] } = {};
      let totalMatches = 0;

      for (const transaction of selectedTransactions) {
        if (transaction.type === 'transfer') continue; // Skip already transfer transactions
        
        console.log(`🔍 Processing transaction ${transaction.id} (${transaction.type} ${Math.abs(transaction.amount || 0)}) from account ${transaction.account_id || transaction.accountId}`);
        
        const accountsData = accounts.map((acc: any) => ({ id: acc.id, currency: acc.currency }));
        const matches = findPotentialTransferMatches(
          transaction as any,
          allTransactionsForDetection as any,
          targetAccountId,
          accountsData
        );
        
        if (matches.length > 0) {
          newMatches[transaction.id] = matches;
          totalMatches += matches.length;
          console.log(`✅ Found ${matches.length} matches for transaction ${transaction.id}`);
        } else {
          // For very old transactions, try relaxed detection criteria
          const transactionDate = new Date(transaction.date || Date.now());
          const transactionAge = (Date.now() - transactionDate.getTime()) / (1000 * 60 * 60 * 24);
          
          if (transactionAge > 30) {
            console.log(`🔍 No matches found for old transaction ${transaction.id}, trying relaxed criteria...`);
            
            try {
              // Import relaxed detection function dynamically
              const { findPotentialTransferMatchesRelaxed } = await import('./utils/transferDetection');
              
              const relaxedMatches = findPotentialTransferMatchesRelaxed(
                transaction as any,
                allTransactionsForDetection as any,
                targetAccountId,
                accountsData,
                {
                  amountTolerancePercentage: transactionAge > 365 ? 15.0 : 10.0, // Higher tolerance for very old transactions
                  minimumConfidence: 50, // Lower confidence threshold
                  exchangeRate: 1.0
                }
              );
              
              if (relaxedMatches.length > 0) {
                newMatches[transaction.id] = relaxedMatches;
                totalMatches += relaxedMatches.length;
                console.log(`✅ Relaxed detection found ${relaxedMatches.length} matches for old transaction ${transaction.id}`);
              }
            } catch (error) {
              console.error('Error with relaxed detection:', error);
            }
          }
        }
      }

      setDetectedMatches(newMatches);
      
      if (totalMatches > 0) {
        console.log(`🎯 Transfer detection complete: ${totalMatches} total matches found for ${Object.keys(newMatches).length} transactions`);
        setShowTransferDetectionSummary(true);
      } else {
        console.log('ℹ️ No transfer matches found');
        
        // Provide helpful information about why no matches were found
        const transactionTypes = selectedTransactions.map(t => t.type);
        const allSameType = transactionTypes.every(type => type === transactionTypes[0]);
        const expectedComplementaryType = transactionTypes[0] === 'expense' ? 'income' : 'expense';
        
        console.log(`💡 Tip: Selected ${selectedTransactions.length} ${transactionTypes[0]} transaction(s). To find matches, there should be ${expectedComplementaryType} transactions of similar amounts on the same dates in the target account.`);
      }
      
    } catch (error) {
      console.error('Error in transfer detection:', error);
      toast.error('Error', {
        description: 'Failed to detect potential transfer matches'
      });
    } finally {
      setIsDetectingTransfers(false);
    }
  };

  // Handler for initiating update with transfer merge option
  const handleInitiateUpdateWithMerge = (shouldMerge: boolean) => {
    if (shouldMerge && Object.keys(detectedMatches).length > 0) {
      // Handle merge with detected matches
      handleInitiateUpdateWithTransferMerge();
    } else {
      // Regular update without merging
      setShowTransferDetectionSummary(false);
      handleInitiateUpdate();
    }
  };

  // Handler for update with transfer merge
  const handleInitiateUpdateWithTransferMerge = async () => {
    if (!user?.id || Object.keys(detectedMatches).length === 0) {
      toast.error('Error', {
        description: 'No transfer matches found to merge'
      });
      return;
    }

    setShowTransferDetectionSummary(false);
    setIsUpdating(true);
    
    const totalOperations = Object.keys(detectedMatches).length;
    let successCount = 0;
    let failedCount = 0;

         // Initialize progress tracking
     setUpdateProgress({
       current: 0,
       total: totalOperations,
       successCount: 0,
       failedCount: 0,
       currentTransaction: ''
     });

    try {
      // Process each transaction with matches
      for (const [transactionId, matches] of Object.entries(detectedMatches)) {
        const sourceTransaction = selectedTransactions.find(t => t.id === transactionId);
        if (!sourceTransaction || matches.length === 0) {
          failedCount++;
          continue;
        }

        // Use the best match (first one, as they're sorted by confidence)
        const bestMatch = matches[0];
        const matchingTransaction = bestMatch.matchingTransaction;

                 // Update progress
         setUpdateProgress(prev => prev ? {
           ...prev,
           current: prev.current + 1,
           currentTransaction: sourceTransaction.description || `Transaction ${sourceTransaction.id}`
         } : null);

        try {
          console.log('Converting to TRANSFER:', {
            'Source amount': `${Math.abs(sourceTransaction.amount || 0)} ${sourceTransaction.account_id}`,
            'Target amount': `${Math.abs(matchingTransaction.amount)} ${matchingTransaction.account_id}`,
            'From account': `${sourceTransaction.account_id}`,
            'To account': `${matchingTransaction.account_id}`
          });
          
          // Check if the convertToTransfer method exists
          if (typeof coreTransactionStore.convertToTransfer !== 'function') {
            console.error('convertToTransfer method not found on coreTransactionStore:', Object.keys(coreTransactionStore));
            throw new Error('Transfer conversion method not available. Please refresh the page.');
          }
          
                     // Use the convertToTransfer method to link existing transactions  
           await coreTransactionStore.convertToTransfer(user.id, sourceTransaction as any, matchingTransaction as any);
          
          successCount++;
          
          // Update progress
          setUpdateProgress(prev => prev ? {
            ...prev,
            successCount: successCount,
            failedCount: failedCount
          } : null);
          
        } catch (error) {
          console.error(`Error converting transaction ${transactionId} to transfer:`, error);
          failedCount++;
          
          // Update progress
          setUpdateProgress(prev => prev ? {
            ...prev,
            failedCount: failedCount
          } : null);
        }
      }

      // Refresh data
      invalidateTransactions(queryClient);
      
      // Call onUpdated callback to refresh the transaction list
      if (onUpdated) {
        try {
          await onUpdated();
        } catch (error) {
          console.error('Error calling onUpdated callback:', error);
        }
      }
      
      // Show completion state
      setIsUpdating(false);
      setIsCompleted(true);
      
      // Create completion summary
      const details: string[] = [];
      if (successCount > 0) {
        details.push(`${successCount} transaction${successCount === 1 ? '' : 's'} converted to transfer${successCount === 1 ? '' : 's'}`);
      }
      if (Object.keys(detectedMatches).length > 0) {
        details.push(`${Object.keys(detectedMatches).length} transfer match${Object.keys(detectedMatches).length === 1 ? '' : 'es'} detected and merged`);
      }
      if (failedCount > 0) {
        details.push(`${failedCount} transaction${failedCount === 1 ? '' : 's'} failed to convert`);
      }
      
      setCompletionSummary({
        type: 'transfer',
        successCount,
        failedCount,
        details
      });

      // Show completion message
      if (failedCount === 0) {
        toast.success(`Successfully merged ${successCount} transaction${successCount === 1 ? '' : 's'} into transfers`);
      } else {
        toast.error(`Merged ${successCount} transactions, ${failedCount} failed`);
      }
      
    } catch (error) {
      console.error('Error in bulk transfer merge:', error);
      toast.error('Error', {
        description: 'Failed to merge transfers. Please try again.'
      });
      // Still show completion state even on error
      setIsUpdating(false);
      setIsCompleted(true);
      setCompletionSummary({
        type: 'transfer',
        successCount: 0,
        failedCount: selectedTransactions.length,
        details: ['Transfer merge operation failed']
      });
    }
  };
  
  // Validate the form data
  const validateForm = (): boolean => {
    const newErrors: Partial<Record<keyof UpdateData, string>> = {}
    
    // Validate amount if selected
    if (selectedFields.amount) {
      const amountValue = parseFloat(updateData.amount)
      if (isNaN(amountValue) || amountValue <= 0) {
        newErrors.amount = 'Amount must be a positive number'
      }
    }
    
    // Validate transfer-specific fields
    if (selectedFields.type && updateData.type === 'transfer') {
      if (!validateTransferConditions()) {
        return false; // Error already set in validateTransferConditions
      }
      
      if (!updateData.toAccountId) {
        newErrors.toAccountId = 'Please select a destination account for transfer'
      }
      
      if (updateData.accountId && updateData.toAccountId && updateData.accountId === updateData.toAccountId) {
        newErrors.toAccountId = 'Source and destination accounts must be different'
      }
    }
    
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }
  
  // Get the list of selected field names for confirmation dialog
  const getSelectedFieldNames = (): string[] => {
    const fieldLabels: Record<keyof UpdateFields, string> = {
      category: `Category: ${updateData.category || 'None'}`,
      account: 'Account',
      tags: `Tags: ${updateData.tags.length ? updateData.tags.join(', ') : 'None'}`,
      type: updateData.type === 'transfer' 
        ? `Type: ${updateData.type} (To: ${accounts.find(a => a.id === updateData.toAccountId)?.name || 'Unknown'})`
        : `Type: ${updateData.type}`,
      description: `Description: ${updateData.description || 'None'}`,
      date: `Date: ${updateData.date ? updateData.date.toLocaleDateString() : 'None'}`,
      notes: 'Notes',
      amount: `Amount: ${updateData.amount || '0'}`,
      status: `Status: ${updateData.status}`,
      payee: 'Payee'
    }
    
    return Object.entries(selectedFields)
      .filter(([_, isSelected]) => isSelected)
      .map(([field]) => fieldLabels[field as keyof UpdateFields])
  }
  
  // Handler for initiating the update process
  const handleInitiateUpdate = () => {
    if (!validateForm()) {
      return
    }
    
    setShowConfirmation(true)
  }
  
  // Handler for the actual update process
  const handleUpdate = async () => {
    setShowConfirmation(false)
    
    if (selectedTransactions.length === 0) {
      toast.error('No transactions selected')
      return
    }
    
    if (!Object.values(selectedFields).some(Boolean)) {
      toast.error('No fields selected for update')
      return
    }
    
    setIsUpdating(true)
    
    // Initialize progress tracking
    setUpdateProgress({
      current: 0,
      total: selectedTransactions.length,
      successCount: 0,
      failedCount: 0,
      currentTransaction: selectedTransactions[0]?.description || 'Transaction'
    })
    
    // Prepare update data
    const updates: Record<string, any> = {}
    
    // Only add fields to updates that are explicitly selected
    if (selectedFields.category && updateData.categoryId) {
      updates.category_id = updateData.categoryId
    }
    
    if (selectedFields.account && updateData.accountId) {
      updates.account_id = updateData.accountId
    }
    
    if (selectedFields.tags) {
      updates.tags = updateData.tags
    }
    
    if (selectedFields.type) {
      updates.type = updateData.type
    }
    
    if (selectedFields.description) {
      updates.description = updateData.description
    }
    
    if (selectedFields.date && updateData.date) {
      updates.date = updateData.date.toISOString()
    }
    
    if (selectedFields.notes) {
      updates.notes = updateData.notes
    }
    
    if (selectedFields.amount && updateData.amount) {
      let amountValue = parseFloat(updateData.amount)
      
      // Make sure amount is properly signed based on transaction type
      // If we're updating type, use the selected type
      if (selectedFields.type) {
        // Ensure amount is positive for income and negative for expense
        amountValue = Math.abs(amountValue) * (updateData.type === 'income' ? 1 : -1)
        updates.amount = amountValue
      } else {
        // If we're not updating type, we need to process each transaction individually
        // This will be handled in the transaction loop below
        updates.amount = amountValue
      }
    }
    
    if (selectedFields.status) {
      updates.status = updateData.status
    }
    
    if (selectedFields.payee && updateData.payeeId) {
      updates.payee_id = updateData.payeeId
    }
    
    // Validate one more time that we're only updating selected fields
    Object.keys(updates).forEach(key => {
      // Convert field_name to fieldName format for checking against selectedFields
      const fieldName = key.includes('_') 
        ? key.replace(/_([a-z])/g, (g) => g[1].toUpperCase()) 
        : key;
      
      // Special cases for field name mapping
      const mappedFieldName = 
        key === 'category_id' ? 'category' :
        key === 'account_id' ? 'account' :
        key === 'payee_id' ? 'payee' :
        fieldName;
      
      if (!selectedFields[mappedFieldName as keyof UpdateFields]) {
        delete updates[key];
      }
    });

    // Log the updates for debugging
    console.log('Updating transactions with:', updates);
    
    // Check if we're converting to transfer type
    const isConvertingToTransfer = selectedFields.type && updateData.type === 'transfer';
    
    // Process each transaction
    let successCount = 0
    let failedCount = 0
    
    if (isConvertingToTransfer) {
      // Special handling for transfer conversion
      try {
        // Validate that all transactions meet transfer requirements
        const firstTransaction = selectedTransactions[0];
        const commonAccountId = firstTransaction.accountId || firstTransaction.account_id;
        const isExpenseDirection = firstTransaction.amount! < 0;
        
        // Update progress for transfer conversion
        setUpdateProgress({
          current: 0,
          total: selectedTransactions.length,
          successCount: 0,
          failedCount: 0,
          currentTransaction: 'Converting to transfers...'
        })
        
        // Process each transaction for transfer conversion
        for (let i = 0; i < selectedTransactions.length; i++) {
          const transaction = selectedTransactions[i];
          
          try {
            // Update progress
            setUpdateProgress({
              current: i,
              total: selectedTransactions.length,
              successCount,
              failedCount,
              currentTransaction: transaction.description || `Transaction ${i + 1}`
            })
            
            // Delete the existing transaction first
            await coreTransactionStore.deleteTransaction(user!.id, transaction.id);
            
            // Get account information
            const fromAccount = accounts.find(a => a.id === commonAccountId);
            const toAccount = accounts.find(a => a.id === updateData.toAccountId);
            
            if (!fromAccount || !toAccount) {
              throw new Error('Could not find specified accounts');
            }
            
            // Calculate the transfer amount and exchange rate
            const transferAmount = Math.abs(parseFloat(updateData.amount || transaction.amount!.toString()));
            const exchangeRate = updateData.exchangeRate || 1;
            
                                      // Determine account direction based on original transaction type
             let finalFromAccountId: string, finalToAccountId: string;
             
             if (isExpenseDirection) {
               // Original was expense - current account is FROM, selected account is TO
               finalFromAccountId = commonAccountId!;
               finalToAccountId = updateData.toAccountId;
             } else {
               // Original was income - selected account is FROM, current account is TO
               finalFromAccountId = updateData.toAccountId;
               finalToAccountId = commonAccountId!;
             }
             
             // Validate account IDs
             if (!finalFromAccountId || !finalToAccountId) {
               throw new Error('Invalid account IDs for transfer');
             }
             
             console.log(`Converting transaction ${transaction.id} to TRANSFER:
  - Source amount: ${transferAmount} ${fromAccount?.currency || 'USD'}
  - Exchange rate: ${exchangeRate}
  - From account: ${finalFromAccountId} (${fromAccount?.name || 'Unknown'})
  - To account: ${finalToAccountId} (${toAccount?.name || 'Unknown'})
`);
             
             // Create the transfer using the core store
             await coreTransactionStore.createTransfer(user!.id, {
               fromAccountId: finalFromAccountId,
               toAccountId: finalToAccountId,
              amount: transferAmount,
              exchangeRate: exchangeRate,
              date: updateData.date ? updateData.date.toISOString() : transaction.date as any,
              description: updateData.description || transaction.description || '',
              notes: updateData.notes || transaction.notes || '',
              tags: updateData.tags.length > 0 ? updateData.tags : (transaction.tags || [])
            });
            
            successCount++;
          } catch (error) {
            console.error(`Failed to convert transaction ${transaction.id} to transfer:`, error);
            failedCount++;
          }
          
          // Update progress after each transaction
          setUpdateProgress({
            current: i + 1,
            total: selectedTransactions.length,
            successCount,
            failedCount,
            currentTransaction: i < selectedTransactions.length - 1 
              ? (selectedTransactions[i + 1]?.description || `Transaction ${i + 2}`)
              : ''
          })
        }
        
        // Invalidate transactions query to refresh the list
        invalidateTransactions(queryClient);
        
        // Force refresh the transaction list to ensure UI shows latest updates
        try {
          console.log('Mass transfer conversion completed, refreshing transaction list...');
          if (user?.id) {
            await fetchTransactions(user.id, {
              page: 1,
              pageSize: 200,
              orderBy: 'date',
              orderDirection: 'desc'
            });
            console.log('Transaction list refreshed after mass transfer conversion');
          }
        } catch (refreshError) {
          console.error('Error refreshing transaction list after mass transfer conversion:', refreshError);
        }
        
      } catch (error) {
        console.error('Error in transfer conversion:', error);
        toast.error("Error", {
          description: "Failed to convert transactions to transfers. Please try again."
        });
      }
    } else {
      // Regular update (not converting to transfer)
      for (let i = 0; i < selectedTransactions.length; i++) {
        const transaction = selectedTransactions[i]
        
        try {
          // Update progress
          setUpdateProgress({
            current: i,
            total: selectedTransactions.length,
            successCount,
            failedCount,
            currentTransaction: transaction.description || `Transaction ${i + 1}`
          })
          
          // Create a transaction-specific update object
          const transactionUpdates = { ...updates }
          
          // If we're updating amount, ensure proper sign based on transaction type
          if (selectedFields.amount && transactionUpdates.amount !== undefined) {
            // Always ensure expenses have negative amounts and income has positive amounts
            const currentType = selectedFields.type ? updateData.type : transaction.type
            
            // Ensure proper sign
            const absAmount = Math.abs(transactionUpdates.amount)
            transactionUpdates.amount = currentType === 'income' ? absAmount : -absAmount
            
            // Add debugging
            console.log(`Transaction ${transaction.id} - Type: ${currentType}, Final amount: ${transactionUpdates.amount}`)
          }
          
          // IMPORTANT: Always explicitly preserve the transaction type if we're not changing it
          // This prevents any potential backend logic from changing types inadvertently
          if (!selectedFields.type) {
            // Explicitly set the type to the transaction's current type to prevent any changes
            transactionUpdates.type = transaction.type
            console.log(`Preserving transaction ${transaction.id} type as: ${transaction.type}`)
          }
          
          // Update the transaction
          await updateTransaction(transaction.id, transactionUpdates)
          
          successCount++
        } catch (error) {
          console.error(`Failed to update transaction ${transaction.id}:`, error)
          failedCount++
        }
        
        // Update progress after each transaction
        setUpdateProgress({
          current: i + 1,
          total: selectedTransactions.length,
          successCount,
          failedCount,
          currentTransaction: i < selectedTransactions.length - 1 
            ? (selectedTransactions[i + 1]?.description || `Transaction ${i + 2}`)
            : ''
        })
      }
    }
    
    // Force refresh the transaction list to ensure UI shows latest updates
    try {
      console.log('Mass updates completed, refreshing transaction list...');
      if (user?.id) {
        await fetchTransactions(user.id, {
          page: 1,
          pageSize: 200,
          orderBy: 'date',
          orderDirection: 'desc'
        });
        console.log('Transaction list refreshed after mass updates');
      }
    } catch (refreshError) {
      console.error('Error refreshing transaction list after mass updates:', refreshError);
    }
    
    // Call onUpdated callback to refresh the parent transaction list
    if (onUpdated) {
      try {
        await onUpdated();
      } catch (error) {
        console.error('Error calling onUpdated callback:', error);
      }
    }
    
    // Show completion state instead of closing immediately
    setIsUpdating(false)
    setIsCompleted(true)
    
    // Create completion summary
    const details: string[] = []
    if (successCount > 0) {
      details.push(`${successCount} transaction${successCount === 1 ? '' : 's'} updated successfully`)
    }
    if (failedCount > 0) {
      details.push(`${failedCount} transaction${failedCount === 1 ? '' : 's'} failed to update`)
    }
    
    setCompletionSummary({
      type: 'update',
      successCount,
      failedCount,
      details
    })
    
    // Show toast notification with results
    if (failedCount === 0) {
      toast.success(`Successfully updated ${successCount} transactions`)
    } else {
      toast.error(`Updated ${successCount} transactions, ${failedCount} failed`)
    }
  }
  
  return (
    <>
      <StyledModal
        isOpen={isOpen}
        onClose={onClose}
        title={isCompleted ? 'Operation Complete' : `Update ${selectedTransactions.length} ${selectedTransactions.length === 1 ? 'Transaction' : 'Transactions'}`}
        maxWidth="800px"
        footer={!isCompleted ? (
          <div className="flex gap-3 w-full">
            <StyledModalButton
              variant="outline"
              onClick={onClose}
              disabled={isUpdating}
              icon={<X className="w-4 h-4" />}
            >
              Cancel
            </StyledModalButton>
            
            {/* Only show regular Update button when not showing transfer detection summary */}
            {!showTransferDetectionSummary && (
              <StyledModalButton
                variant="primary"
                onClick={handleInitiateUpdate}
                disabled={isUpdating || !Object.values(selectedFields).some(Boolean) || isDetectingTransfers}
                icon={<Check className="w-4 h-4" />}
              >
                {isUpdating ? 'Updating...' : isDetectingTransfers ? 'Detecting...' : 'Update'}
              </StyledModalButton>
            )}
          </div>
        ) : undefined}
      >
        {/* Description */}
        {!isCompleted && (
          <StyledModalSection 
            title="Instructions"
            icon={<Settings className="h-4 w-4" />}
            className="mb-4"
          >
            <p className="text-sm text-[#2F2F2F] dark:text-[#A3A3A3]">
              Select the fields you want to update for the selected transactions. Only the checked fields will be modified.
            </p>
          </StyledModalSection>
        )}

        {/* Error alert */}
        {!isCompleted && Object.keys(errors).length > 0 && (
          <StyledModalSection 
            title="Validation Errors"
            icon={<AlertTriangle className="h-4 w-4" />}
            primaryColor="#E53935"
            className="mb-4"
          >
            <ul className="text-sm text-[#E53935] dark:text-[#EF5350] space-y-1">
              {Object.entries(errors).map(([field, error]) => (
                <li key={field} className="flex items-start gap-2">
                  <span className="w-1.5 h-1.5 bg-[#E53935] dark:bg-[#EF5350] rounded-full mt-2 flex-shrink-0"></span>
                  {error}
                </li>
              ))}
            </ul>
          </StyledModalSection>
        )}
        
        {/* Progress tracker */}
        {isUpdating && updateProgress && (
          <StyledModalSection 
            title="Update Progress"
            icon={<Target className="h-4 w-4" />}
            className="mb-4"
          >
            <UpdateProgressTracker
              current={updateProgress.current}
              total={updateProgress.total}
              successCount={updateProgress.successCount}
              failedCount={updateProgress.failedCount}
              currentTransaction={updateProgress.currentTransaction}
            />
          </StyledModalSection>
        )}

        {/* Transfer Detection Summary */}
        {showTransferDetectionSummary && Object.keys(detectedMatches).length > 0 && !isUpdating && (
          <StyledModalSection 
            title="Transfer Detection Results"
            icon={<ArrowRightLeft className="h-4 w-4" />}
            primaryColor="#439476"
            className="mb-4"
          >
            <div className="space-y-4">
              <div className="flex items-center gap-2 text-sm text-[#439476] dark:text-[#439476]">
                <CheckCircle className="w-4 h-4" />
                <span>
                  Found potential transfer matches for {Object.keys(detectedMatches).length} out of {selectedTransactions.length} transaction(s)
                </span>
              </div>
              
              {/* Detailed Transaction Breakdown */}
              <div className="space-y-3">
                {/* Matched Transactions */}
                {Object.keys(detectedMatches).length > 0 && (
                  <div className="bg-[#439476]/5 dark:bg-[#439476]/10 rounded-lg p-3 border border-[#439476]/15">
                    <div className="text-sm font-medium text-[#439476] dark:text-[#439476] mb-2 flex items-center gap-1">
                      <CheckCircle className="w-3 h-3" />
                      Transactions with Matches ({Object.keys(detectedMatches).length})
                    </div>
                    <div className="space-y-2">
                      {Object.entries(detectedMatches).map(([transactionId, matches]) => {
                        const transaction = selectedTransactions.find(t => t.id === transactionId);
                        const bestMatch = matches[0];
                        return (
                          <div key={transactionId} className="text-xs bg-white/50 dark:bg-[#1E2B42]/30 rounded p-2">
                            <div className="flex justify-between items-start mb-1">
                              <span className="font-medium text-[#1C1C1C] dark:text-[#E8E8E8]">
                                {transaction?.description || 'Unknown Transaction'}
                              </span>
                              <span className="text-[#439476] font-medium">
                                {bestMatch.confidence}% match
                              </span>
                            </div>
                            <div className="flex justify-between text-[#2F2F2F] dark:text-[#A3A3A3]">
                              <span>{Math.abs(transaction?.amount || 0).toFixed(2)} ({transaction?.type})</span>
                              <span>→ {Math.abs(bestMatch.matchingTransaction.amount).toFixed(2)} ({bestMatch.matchingTransaction.type})</span>
                            </div>
                            <div className="text-[#2F2F2F]/70 dark:text-[#A3A3A3]/70 text-[10px]">
                              Match: {bestMatch.matchingTransaction.description || 'No description'}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
                
                {/* Unmatched Transactions */}
                {selectedTransactions.filter(t => !detectedMatches[t.id]).length > 0 && (
                  <div className="bg-[#2F2F2F]/5 dark:bg-[#2F2F2F]/10 rounded-lg p-3 border border-[#2F2F2F]/15">
                    <div className="text-sm font-medium text-[#2F2F2F] dark:text-[#A3A3A3] mb-2 flex items-center gap-1">
                      <X className="w-3 h-3" />
                      Transactions without Matches ({selectedTransactions.filter(t => !detectedMatches[t.id]).length})
                    </div>
                    <div className="space-y-2">
                      {selectedTransactions.filter(t => !detectedMatches[t.id]).map((transaction) => (
                        <div key={transaction.id} className="text-xs bg-white/50 dark:bg-[#1E2B42]/30 rounded p-2">
                          <div className="flex justify-between items-center">
                            <span className="font-medium text-[#1C1C1C] dark:text-[#E8E8E8]">
                              {transaction.description || 'Unknown Transaction'}
                            </span>
                            <span className="text-[#2F2F2F] dark:text-[#A3A3A3]">
                              {Math.abs(transaction.amount || 0).toFixed(2)} ({transaction.type})
                            </span>
                          </div>
                          <div className="text-[#2F2F2F]/70 dark:text-[#A3A3A3]/70 text-[10px]">
                            No matching {transaction.type === 'expense' ? 'income' : 'expense'} found in target account
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              
              <div className="bg-[#439476]/10 dark:bg-[#439476]/20 rounded-lg p-3 border border-[#439476]/20">
                <div className="text-sm text-[#2F2F2F] dark:text-[#A3A3A3] mb-2">
                  <strong>Options:</strong>
                </div>
                <div className="space-y-2 text-sm">
                  <div className="flex items-start gap-2">
                    <span className="w-1.5 h-1.5 bg-[#439476] rounded-full mt-2 flex-shrink-0"></span>
                    <span className="text-[#2F2F2F] dark:text-[#A3A3A3]">
                      <strong>Convert and Merge:</strong> Convert selected transactions to transfers and automatically merge them with detected matches (recommended)
                    </span>
                  </div>
                  <div className="flex items-start gap-2">
                    <span className="w-1.5 h-1.5 bg-[#2F2F2F]/40 rounded-full mt-2 flex-shrink-0"></span>
                    <span className="text-[#2F2F2F] dark:text-[#A3A3A3]">
                      <strong>Convert Only:</strong> Convert to transfers without merging (will create separate transfer transactions)
                    </span>
                  </div>
                </div>
              </div>
              
              <div className="flex gap-2">
                <StyledModalButton
                  variant="primary"
                  onClick={() => handleInitiateUpdateWithMerge(true)}
                  disabled={isUpdating}
                  icon={<ArrowRightLeft className="w-4 h-4" />}
                  className="flex-1"
                >
                  Convert & Merge
                </StyledModalButton>
                <StyledModalButton
                  variant="outline"
                  onClick={() => handleInitiateUpdateWithMerge(false)}
                  disabled={isUpdating}
                  className="flex-1"
                >
                  Convert Only
                </StyledModalButton>
              </div>
            </div>
          </StyledModalSection>
        )}

        {/* Transfer Detection Loading */}
        {isDetectingTransfers && (
          <StyledModalSection 
            title="Detecting Transfer Matches"
            icon={<ArrowRightLeft className="h-4 w-4" />}
            primaryColor="#439476"
            className="mb-4"
          >
            <div className="flex items-center gap-3 text-sm text-[#2F2F2F] dark:text-[#A3A3A3]">
              <div className="animate-spin rounded-full h-4 w-4 border-2 border-[#439476] border-t-transparent"></div>
              <span>Scanning for potential transfer matches...</span>
            </div>
          </StyledModalSection>
        )}

        {/* Completion Summary */}
        {isCompleted && completionSummary && (
          <div className="space-y-6">
            {/* Hero Success Section */}
            <div className="text-center py-8">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-br from-[#439476] to-[#2d6e54] mb-4 shadow-lg">
                {completionSummary.failedCount === 0 ? (
                  <CheckCircle className="w-8 h-8 text-white" />
                ) : (
                  <AlertTriangle className="w-8 h-8 text-white" />
                )}
              </div>
              <h2 className="text-2xl font-bold text-[#1C1C1C] dark:text-[#E8E8E8] mb-2">
                {completionSummary.failedCount === 0 ? 'Operation Successful!' : 'Operation Completed'}
              </h2>
              <p className="text-[#2F2F2F] dark:text-[#A3A3A3] text-base">
                {completionSummary.type === 'transfer' ? 'Transfer operation' : 'Update operation'} has been completed successfully
              </p>
            </div>

            {/* Results Summary Cards */}
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="bg-gradient-to-br from-[#439476]/10 to-[#439476]/5 dark:from-[#439476]/20 dark:to-[#439476]/10 rounded-xl p-6 border border-[#439476]/20">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-8 h-8 rounded-lg bg-[#439476]/20 flex items-center justify-center">
                    <CheckCircle className="w-4 h-4 text-[#439476]" />
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-[#439476]">
                      {completionSummary.successCount}
                    </div>
                    <div className="text-sm font-medium text-[#2F2F2F] dark:text-[#A3A3A3]">
                      Successful
                    </div>
                  </div>
                </div>
              </div>
              {completionSummary.failedCount > 0 && (
                <div className="bg-gradient-to-br from-[#E53935]/10 to-[#E53935]/5 dark:from-[#E53935]/20 dark:to-[#E53935]/10 rounded-xl p-6 border border-[#E53935]/20">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-8 h-8 rounded-lg bg-[#E53935]/20 flex items-center justify-center">
                      <AlertTriangle className="w-4 h-4 text-[#E53935]" />
                    </div>
                    <div>
                      <div className="text-2xl font-bold text-[#E53935]">
                        {completionSummary.failedCount}
                      </div>
                      <div className="text-sm font-medium text-[#2F2F2F] dark:text-[#A3A3A3]">
                        Failed
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Task Details */}
            <div className="bg-white/50 dark:bg-[#1E2B42]/30 backdrop-blur-sm rounded-xl p-6 border border-[#E5E7EB] dark:border-[#374151]">
              <h3 className="text-lg font-semibold text-[#1C1C1C] dark:text-[#E8E8E8] mb-4 flex items-center gap-2">
                <Target className="w-5 h-5 text-[#439476]" />
                Tasks Completed
              </h3>
              <div className="space-y-3">
                {completionSummary.details.map((detail, index) => (
                  <div key={index} className="flex items-start gap-3 p-3 bg-white/70 dark:bg-[#1E2B42]/50 rounded-lg border border-[#E5E7EB]/50 dark:border-[#374151]/50">
                    <div className="w-6 h-6 rounded-full bg-[#439476]/15 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <CheckCircle className="w-3 h-3 text-[#439476]" />
                    </div>
                    <span className="text-[#2F2F2F] dark:text-[#A3A3A3] font-medium">{detail}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Action Button */}
            <div className="flex justify-center pt-6">
              <StyledModalButton
                variant="primary"
                onClick={onClose}
                icon={<Check className="w-4 h-4" />}
                className="px-8 py-3 text-base font-semibold shadow-lg hover:shadow-xl transition-all duration-200"
              >
                Done
              </StyledModalButton>
            </div>
          </div>
        )}

        {!isUpdating && !isCompleted && (
          <div className="space-y-6">
            {/* Category & Account Section */}
            <StyledModalSection 
              title="Basic Information"
              icon={<FileEdit className="h-4 w-4" />}
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                {/* Category Section */}
                <div className="space-y-4">
                  <div className="flex items-center space-x-3">
                    <Checkbox
                      id="category"
                      checked={selectedFields.category}
                      onCheckedChange={(checked) => handleFieldToggle('category', checked as boolean)}
                      className="h-4 w-4 border-secondary text-secondary focus:ring-secondary dark:border-primary-dark dark:text-primary-dark dark:focus:ring-primary-dark"
                    />
                    <Label 
                      htmlFor="category"
                      className="text-sm font-medium text-[#1C1C1C] dark:text-[#E8E8E8]"
                    >
                      Update Category
                    </Label>
                  </div>
                  {selectedFields.category && (
                    <div className="pl-7">
                      <CategorySelect
                        value={{
                          categoryId: updateData.categoryId,
                          category: updateData.category
                        }}
                        onChange={handleCategoryChange}
                        typeFilter={null}
                      />
                    </div>
                  )}
                </div>

                {/* Account Section */}
                <div className="space-y-4">
                  <div className="flex items-center space-x-3">
                    <Checkbox
                      id="account"
                      checked={selectedFields.account}
                      onCheckedChange={(checked) => handleFieldToggle('account', checked as boolean)}
                      className="h-4 w-4 border-secondary text-secondary focus:ring-secondary dark:border-primary-dark dark:text-primary-dark dark:focus:ring-primary-dark"
                    />
                    <Label 
                      htmlFor="account"
                      className="text-sm font-medium text-[#1C1C1C] dark:text-[#E8E8E8]"
                    >
                      Update Account
                    </Label>
                  </div>
                  {selectedFields.account && (
                    <div className="pl-7">
                      <AccountSelect
                        value={updateData.accountId}
                        onChange={handleAccountChange}
                      />
                    </div>
                  )}
                </div>
              </div>
            </StyledModalSection>

            {/* Type & Amount Section */}
            <StyledModalSection 
              title="Transaction Details"
              icon={<DollarSign className="h-4 w-4" />}
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                {/* Transaction Type Section */}
                <div className="space-y-4">
                  <div className="flex items-center space-x-3">
                    <Checkbox
                      id="type"
                      checked={selectedFields.type}
                      onCheckedChange={(checked) => handleFieldToggle('type', checked as boolean)}
                      className="h-4 w-4 border-secondary text-secondary focus:ring-secondary dark:border-primary-dark dark:text-primary-dark dark:focus:ring-primary-dark"
                    />
                    <Label 
                      htmlFor="type"
                      className="text-sm font-medium text-[#1C1C1C] dark:text-[#E8E8E8]"
                    >
                      Update Type
                    </Label>
                  </div>
                  {selectedFields.type && (
                    <div className="pl-7">
                      <RadioGroup
                        value={updateData.type}
                        onValueChange={(value) => handleTypeChange(value as 'expense' | 'income' | 'transfer')}
                        className="flex flex-wrap gap-4"
                      >
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem 
                            value="expense" 
                            id="expense"
                            className="border-secondary text-secondary focus:ring-secondary dark:border-primary-dark dark:text-primary-dark dark:focus:ring-primary-dark" 
                          />
                          <Label htmlFor="expense" className="text-sm text-[#1C1C1C] dark:text-[#E8E8E8]">Expense</Label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem 
                            value="income" 
                            id="income"
                            className="border-secondary text-secondary focus:ring-secondary dark:border-primary-dark dark:text-primary-dark dark:focus:ring-primary-dark" 
                          />
                          <Label htmlFor="income" className="text-sm text-[#1C1C1C] dark:text-[#E8E8E8]">Income</Label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem 
                            value="transfer" 
                            id="transfer"
                            className="border-secondary text-secondary focus:ring-secondary dark:border-primary-dark dark:text-primary-dark dark:focus:ring-primary-dark" 
                          />
                          <Label htmlFor="transfer" className="text-sm text-[#1C1C1C] dark:text-[#E8E8E8]">Transfer</Label>
                        </div>
                      </RadioGroup>
                    </div>
                  )}
                </div>

                {/* Amount Section */}
                <div className="space-y-4">
                  <div className="flex items-center space-x-3">
                    <Checkbox
                      id="amount"
                      checked={selectedFields.amount}
                      onCheckedChange={(checked) => handleFieldToggle('amount', checked as boolean)}
                      className="h-4 w-4 border-secondary text-secondary focus:ring-secondary dark:border-primary-dark dark:text-primary-dark dark:focus:ring-primary-dark"
                    />
                    <Label 
                      htmlFor="amount"
                      className="text-sm font-medium text-[#1C1C1C] dark:text-[#E8E8E8]"
                    >
                      Update Amount
                    </Label>
                  </div>
                  {selectedFields.amount && (
                    <div className="pl-7">
                      <div className="relative">
                        <div className="flex items-center relative">
                          <DollarSign className="w-4 h-4 text-[#2F2F2F] dark:text-[#A3A3A3] absolute left-3" />
                          <Input
                            type="number"
                            placeholder="Enter amount"
                            value={updateData.amount}
                            onChange={(e) => handleTextChange('amount', e.target.value)}
                            className={cn(
                              "pl-9 h-11 w-full rounded-xl border shadow-sm focus:ring-2 bg-white dark:bg-[#1E2B42] text-[#1C1C1C] dark:text-[#E8E8E8]",
                              errors.amount 
                                ? "border-[#E53935] focus:ring-[#E53935]" 
                                : "border-[#2F2F2F]/10 dark:border-[#333840] focus:ring-[#439476]"
                            )}
                            min="0.01"
                            step="0.01"
                          />
                        </div>
                        {errors.amount && (
                          <p className="text-xs text-[#E53935] mt-1">{errors.amount}</p>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Transfer Fields Section */}
              {selectedFields.type && updateData.type === 'transfer' && (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6 pt-6 border-t border-[#2F2F2F]/10 dark:border-[#333840]">
                  {/* To Account Field */}
                  <div>
                    <label className="block text-sm font-medium text-[#1C1C1C] dark:text-[#E8E8E8] mb-1.5">
                      To Account
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <CreditCard className="h-4 w-4 text-[#2F2F2F] dark:text-[#A3A3A3]" />
                      </div>
                      <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                        <ChevronDown className="h-4 w-4 text-[#2F2F2F] dark:text-[#A3A3A3]" />
                      </div>
                      <select
                        value={updateData.toAccountId}
                        onChange={(e) => handleToAccountChange(e.target.value)}
                        className={cn(
                          "block w-full pl-10 pr-10 py-2.5 text-[#1C1C1C] dark:text-[#E8E8E8] ring-1 ring-inset rounded-xl sm:text-sm bg-white dark:bg-[#1E2B42] transition-colors appearance-none h-10",
                          errors.toAccountId 
                            ? "ring-[#E53935] focus:ring-[#E53935]" 
                            : "ring-[#2F2F2F]/10 dark:ring-[#333840] focus:ring-2 focus:ring-[#439476]"
                        )}
                        required
                      >
                        <option value="">Select destination account</option>
                        {Array.isArray(accounts) && accounts
                          .sort((a, b) => a.name.localeCompare(b.name))
                          .filter(account => account.id !== updateData.accountId)
                          .map((account) => (
                            <option key={account.id} value={account.id}>
                              {account.name}
                            </option>
                          ))}
                      </select>
                      {errors.toAccountId && (
                        <p className="mt-1 text-xs text-[#E53935]">{errors.toAccountId}</p>
                      )}
                    </div>
                  </div>

                  {/* Exchange Rate Input */}
                  {updateData.accountId && updateData.toAccountId && (
                    <div>
                      <ExchangeRateInput
                        fromAccountId={updateData.accountId}
                        toAccountId={updateData.toAccountId}
                        exchangeRate={updateData.exchangeRate}
                        onExchangeRateChange={handleExchangeRateChange}
                        onFetchLatestRate={async () => {
                          try {
                            if (!user?.id) {
                              toast.error("Error", {
                                description: "User not authenticated"
                              });
                              return;
                            }
                            
                            const fromAccount = accounts.find(acc => acc.id === updateData.accountId);
                            const toAccount = accounts.find(acc => acc.id === updateData.toAccountId);
                            
                            if (!fromAccount?.currency || !toAccount?.currency) {
                              toast.error("Error", {
                                description: "Currency information is missing for one or both accounts"
                              });
                              return;
                            }

                            if (fromAccount.currency === toAccount.currency) {
                              handleExchangeRateChange(1);
                              return;
                            }

                            try {
                              const rate = coreConvertAmount(1, fromAccount.currency, toAccount.currency);
                              if (rate && !isNaN(rate)) {
                                const roundedRate = Math.round(rate * 1000000) / 1000000;
                                handleExchangeRateChange(roundedRate);
                                
                                toast.success("Success", {
                                  description: `Exchange rate updated: 1 ${fromAccount.currency} = ${roundedRate} ${toAccount.currency}`
                                });
                              }
                            } catch (error) {
                              console.error('Error calculating exchange rate:', error);
                              toast.error("Error", {
                                description: "Failed to calculate exchange rate. Please enter it manually."
                              });
                            }
                          } catch (error) {
                            console.error('Error updating exchange rate:', error);
                            toast.error("Error", {
                              description: "Failed to update exchange rate"
                            });
                          }
                        }}
                        className="mt-2"
                      />
                    </div>
                  )}
                </div>
              )}
            </StyledModalSection>

            {/* Description & Date Section */}
            <StyledModalSection 
              title="Additional Information"
              icon={<CalendarIcon className="h-4 w-4" />}
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                {/* Description Section */}
                <div className="space-y-4">
                  <div className="flex items-center space-x-3">
                    <Checkbox
                      id="description"
                      checked={selectedFields.description}
                      onCheckedChange={(checked) => handleFieldToggle('description', checked as boolean)}
                      className="h-4 w-4 border-secondary text-secondary focus:ring-secondary dark:border-primary-dark dark:text-primary-dark dark:focus:ring-primary-dark"
                    />
                    <Label 
                      htmlFor="description"
                      className="text-sm font-medium text-[#1C1C1C] dark:text-[#E8E8E8]"
                    >
                      Update Description
                    </Label>
                  </div>
                  {selectedFields.description && (
                    <div className="pl-7">
                      <div className="relative">
                        <div className="flex items-center relative">
                          <FileEdit className="w-4 h-4 text-[#2F2F2F] dark:text-[#A3A3A3] absolute left-3" />
                          <Input
                            placeholder="Enter description"
                            value={updateData.description}
                            onChange={(e) => handleTextChange('description', e.target.value)}
                            className="pl-9 h-11 w-full rounded-xl border border-[#2F2F2F]/10 dark:border-[#333840] bg-white dark:bg-[#1E2B42] shadow-sm focus:ring-2 focus:ring-[#439476] text-[#1C1C1C] dark:text-[#E8E8E8]"
                          />
                        </div>
                      </div>
                    </div>
                  )}
                </div>
                
                {/* Date Section */}
                <div className="space-y-4">
                  <div className="flex items-center space-x-3">
                    <Checkbox
                      id="date"
                      checked={selectedFields.date}
                      onCheckedChange={(checked) => handleFieldToggle('date', checked as boolean)}
                      className="h-4 w-4 border-secondary text-secondary focus:ring-secondary dark:border-primary-dark dark:text-primary-dark dark:focus:ring-primary-dark"
                    />
                    <Label 
                      htmlFor="date"
                      className="text-sm font-medium text-[#1C1C1C] dark:text-[#E8E8E8]"
                    >
                      Update Date
                    </Label>
                  </div>
                  {selectedFields.date && (
                    <div className="pl-7">
                      <div className="relative">
                        <DatePicker 
                          date={updateData.date} 
                          setDate={handleDateChange}
                          className="w-full"
                          minDate={new Date(new Date().setFullYear(new Date().getFullYear() - 5))}
                          maxDate={new Date(new Date().setFullYear(new Date().getFullYear() + 1))}
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </StyledModalSection>

            {/* Tags & Payee Section */}
            <StyledModalSection 
              title="Tags & Payee"
              icon={<Target className="h-4 w-4" />}
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                {/* Tags Section */}
                <div className="space-y-4">
                  <div className="flex items-center space-x-3">
                    <Checkbox
                      id="tags"
                      checked={selectedFields.tags}
                      onCheckedChange={(checked) => handleFieldToggle('tags', checked as boolean)}
                      className="h-4 w-4 border-secondary text-secondary focus:ring-secondary dark:border-primary-dark dark:text-primary-dark dark:focus:ring-primary-dark"
                    />
                    <Label 
                      htmlFor="tags"
                      className="text-sm font-medium text-[#1C1C1C] dark:text-[#E8E8E8]"
                    >
                      Update Tags
                    </Label>
                  </div>
                  {selectedFields.tags && (
                    <div className="pl-7">
                      <div className="relative" style={{ zIndex: 100 }}>
                        <TagSelect
                          value={updateData.tags}
                          onChange={handleTagsChange}
                        />
                      </div>
                    </div>
                  )}
                </div>

                {/* Payee Section */}
                <div className="space-y-4">
                  <div className="flex items-center space-x-3">
                    <Checkbox
                      id="payee"
                      checked={selectedFields.payee}
                      onCheckedChange={(checked) => handleFieldToggle('payee', checked as boolean)}
                      className="h-4 w-4 border-secondary text-secondary focus:ring-secondary dark:border-primary-dark dark:text-primary-dark dark:focus:ring-primary-dark"
                    />
                    <Label 
                      htmlFor="payee"
                      className="text-sm font-medium text-[#1C1C1C] dark:text-[#E8E8E8]"
                    >
                      Update Payee
                    </Label>
                  </div>
                  {selectedFields.payee && (
                    <div className="pl-7">
                      <PayeeSelect
                        value={updateData.payeeId}
                        onChange={(value) => setUpdateData(prev => ({ ...prev, payeeId: value }))}
                      />
                    </div>
                  )}
                </div>
              </div>
            </StyledModalSection>

            {/* Status & Notes Section */}
            <StyledModalSection 
              title="Status & Notes"
              icon={<Settings className="h-4 w-4" />}
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                {/* Status Section */}
                <div className="space-y-4">
                  <div className="flex items-center space-x-3">
                    <Checkbox
                      id="status"
                      checked={selectedFields.status}
                      onCheckedChange={(checked) => handleFieldToggle('status', checked as boolean)}
                      className="h-4 w-4 border-secondary text-secondary focus:ring-secondary dark:border-primary-dark dark:text-primary-dark dark:focus:ring-primary-dark"
                    />
                    <Label 
                      htmlFor="status"
                      className="text-sm font-medium text-[#1C1C1C] dark:text-[#E8E8E8]"
                    >
                      Update Status
                    </Label>
                  </div>
                  {selectedFields.status && (
                    <div className="pl-7">
                      <RadioGroup
                        value={updateData.status}
                        onValueChange={(value) => handleStatusChange(value as 'pending' | 'completed' | 'cancelled')}
                        className="flex flex-wrap gap-4"
                      >
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem 
                            value="completed" 
                            id="completed"
                            className="border-secondary text-secondary focus:ring-secondary dark:border-primary-dark dark:text-primary-dark dark:focus:ring-primary-dark" 
                          />
                          <Label htmlFor="completed" className="text-sm text-[#1C1C1C] dark:text-[#E8E8E8]">Completed</Label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem 
                            value="pending" 
                            id="pending"
                            className="border-secondary text-secondary focus:ring-secondary dark:border-primary-dark dark:text-primary-dark dark:focus:ring-primary-dark" 
                          />
                          <Label htmlFor="pending" className="text-sm text-[#1C1C1C] dark:text-[#E8E8E8]">Pending</Label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem 
                            value="cancelled" 
                            id="cancelled"
                            className="border-secondary text-secondary focus:ring-secondary dark:border-primary-dark dark:text-primary-dark dark:focus:ring-primary-dark" 
                          />
                          <Label htmlFor="cancelled" className="text-sm text-[#1C1C1C] dark:text-[#E8E8E8]">Cancelled</Label>
                        </div>
                      </RadioGroup>
                    </div>
                  )}
                </div>

                {/* Notes Section */}
                <div className="space-y-4">
                  <div className="flex items-center space-x-3">
                    <Checkbox
                      id="notes"
                      checked={selectedFields.notes}
                      onCheckedChange={(checked) => handleFieldToggle('notes', checked as boolean)}
                      className="h-4 w-4 border-secondary text-secondary focus:ring-secondary dark:border-primary-dark dark:text-primary-dark dark:focus:ring-primary-dark"
                    />
                    <Label 
                      htmlFor="notes"
                      className="text-sm font-medium text-[#1C1C1C] dark:text-[#E8E8E8]"
                    >
                      Update Notes
                    </Label>
                  </div>
                  {selectedFields.notes && (
                    <div className="pl-7">
                      <div className="relative">
                        <Textarea
                          placeholder="Enter notes"
                          value={updateData.notes}
                          onChange={(e) => handleTextChange('notes', e.target.value)}
                          className="min-h-[100px] rounded-xl border border-[#2F2F2F]/10 dark:border-[#333840] bg-white dark:bg-[#1E2B42] shadow-sm focus:ring-2 focus:ring-[#439476] text-[#1C1C1C] dark:text-[#E8E8E8]"
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </StyledModalSection>
          </div>
        )}
      </StyledModal>
      
      {/* Confirmation Dialog */}
      <UpdateConfirmationDialog
        isOpen={showConfirmation}
        onClose={() => setShowConfirmation(false)}
        onConfirm={handleUpdate}
        transactionCount={selectedTransactions.length}
        selectedFields={getSelectedFieldNames()}
      />
    </>
  )
} 